// Funções puras do envio de PASTAS (input `webkitdirectory` e arrastar-e-soltar de pastas):
// limpeza do caminho relativo, filtro de tipos aceitos / arquivos de sistema, resumo dos
// ignorados e a varredura recursiva das entradas do DataTransfer. Sem DOM — os tipos de
// entrada são estruturais para os testes (Vitest, ambiente node) usarem objetos falsos.
import { MEDIA_MAX_BYTES, guessContentType } from './mediaUploadUtils';

/** Arquivo escolhido + caminho relativo dentro da pasta enviada ('' quando veio solto). */
export interface PickedFile<F = File> {
  file: F;
  /** Ex.: "CLIENTES/Caixas de Couro/foto.jpg" (inclui o nome do arquivo). */
  relativePath: string;
}

/** O mínimo de `File` que os filtros usam. */
export interface FileLike {
  name: string;
  size: number;
  type?: string;
}

export type IgnoreReason = 'system' | 'unsupported' | 'empty' | 'too_large' | 'unreadable';

export interface IgnoredFile {
  name: string;
  /** Caminho relativo (ou o nome, quando veio solto). */
  path: string;
  reason: IgnoreReason;
  /** Extensão em minúsculas, sem ponto ('' se não houver). */
  ext: string;
}

export const IGNORE_REASON_LABELS: Record<IgnoreReason, string> = {
  system: 'arquivo de sistema',
  unsupported: 'tipo não suportado',
  empty: 'arquivo vazio',
  too_large: 'acima de 2 GB',
  unreadable: 'não foi possível ler',
};

/**
 * Normaliza o caminho relativo: barras invertidas viram "/", tira a "/" inicial (do
 * `entry.fullPath`), barras repetidas e segmentos ".".
 */
export const cleanRelativePath = (path: string): string =>
  (path ?? '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s !== '' && s !== '.')
    .join('/');

/**
 * Caminho que vai em `relative_path` no POST /media/uploads: só quando o arquivo veio de
 * dentro de uma pasta (tem ao menos uma "/"); arquivo solto → undefined.
 */
export const relativePathForUpload = (relativePath: string | undefined): string | undefined => {
  const cleaned = cleanRelativePath(relativePath ?? '');
  return cleaned.includes('/') ? cleaned : undefined;
};

/** Extensão em minúsculas, sem o ponto. "foto.JPG" → "jpg"; "LEIAME" → "". */
export const fileExtension = (name: string): string => {
  const base = name.split('/').pop() ?? name;
  const dot = base.lastIndexOf('.');
  return dot > 0 && dot < base.length - 1 ? base.slice(dot + 1).toLowerCase() : '';
};

const SYSTEM_FILE_NAMES = new Set(['.ds_store', 'thumbs.db', 'ehthumbs.db', 'desktop.ini', '.localized', 'icon\r']);
const SYSTEM_DIR_NAMES = new Set(['__macosx', '$recycle.bin', 'system volume information']);

/**
 * Arquivos de sistema/sincronização que nunca devem subir: .DS_Store, Thumbs.db, desktop.ini,
 * "._*" (metadados do macOS), "~$*" (temporários do Office), marcadores ".icloud" e qualquer
 * coisa dentro de pastas ocultas ("."), __MACOSX ou lixeira.
 */
export const isSystemFile = (name: string, relativePath = ''): boolean => {
  const lower = name.toLowerCase();
  if (SYSTEM_FILE_NAMES.has(lower)) return true;
  if (lower.startsWith('._') || lower.startsWith('~$')) return true;
  if (lower.endsWith('.icloud')) return true;
  const dirs = cleanRelativePath(relativePath).split('/').slice(0, -1);
  return dirs.some((d) => d.startsWith('.') || SYSTEM_DIR_NAMES.has(d.toLowerCase()));
};

/** Motivo para ignorar o arquivo, ou null se ele pode entrar na fila. */
export const classifyFile = (file: FileLike, relativePath = ''): IgnoreReason | null => {
  if (isSystemFile(file.name, relativePath)) return 'system';
  if (!guessContentType(file.name, file.type)) return 'unsupported';
  if (!(file.size > 0)) return 'empty';
  if (file.size > MEDIA_MAX_BYTES) return 'too_large';
  return null;
};

/** Separa o que vai para a fila do que é ignorado (em silêncio, só entra no resumo). */
export const partitionPicked = <F extends FileLike>(
  picked: PickedFile<F>[],
): { accepted: PickedFile<F>[]; ignored: IgnoredFile[] } => {
  const accepted: PickedFile<F>[] = [];
  const ignored: IgnoredFile[] = [];
  for (const p of picked) {
    const path = cleanRelativePath(p.relativePath) || p.file.name;
    const reason = classifyFile(p.file, path);
    if (reason) ignored.push({ name: p.file.name, path, reason, ext: fileExtension(p.file.name) });
    else accepted.push({ file: p.file, relativePath: cleanRelativePath(p.relativePath) });
  }
  return { accepted, ignored };
};

export interface IgnoredSummary {
  total: number;
  byReason: Record<IgnoreReason, number>;
  /** Extensões não suportadas, da mais frequente para a menos ("(sem extensão)" se vazia). */
  unsupportedExts: string[];
}

export const summarizeIgnored = (ignored: IgnoredFile[]): IgnoredSummary => {
  const byReason: Record<IgnoreReason, number> = { system: 0, unsupported: 0, empty: 0, too_large: 0, unreadable: 0 };
  const extCount = new Map<string, number>();
  for (const it of ignored) {
    byReason[it.reason]++;
    if (it.reason === 'unsupported') {
      const ext = it.ext || '(sem extensão)';
      extCount.set(ext, (extCount.get(ext) ?? 0) + 1);
    }
  }
  const unsupportedExts = [...extCount.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([ext]) => ext);
  return { total: ignored.length, byReason, unsupportedExts };
};

const fmt = (n: number) => n.toLocaleString('pt-BR');
const plural = (n: number, one: string, many: string) => `${fmt(n)} ${n === 1 ? one : many}`;

/** Quantas extensões aparecem no resumo antes das reticências. */
export const SUMMARY_MAX_EXTS = 5;

/**
 * "120 arquivos na fila, 7 ignorados (tipos não suportados: psd, pdf, zip…; arquivos de sistema: 2)".
 */
export const selectionSummaryText = (queued: number, ignored: IgnoredFile[]): string => {
  const head = plural(queued, 'arquivo na fila', 'arquivos na fila');
  if (ignored.length === 0) return head;
  const s = summarizeIgnored(ignored);
  const parts: string[] = [];
  if (s.unsupportedExts.length > 0) {
    const shown = s.unsupportedExts.slice(0, SUMMARY_MAX_EXTS).join(', ');
    parts.push(`tipos não suportados: ${shown}${s.unsupportedExts.length > SUMMARY_MAX_EXTS ? '…' : ''}`);
  }
  if (s.byReason.system > 0) parts.push(`arquivos de sistema: ${fmt(s.byReason.system)}`);
  if (s.byReason.empty > 0) parts.push(`vazios: ${fmt(s.byReason.empty)}`);
  if (s.byReason.too_large > 0) parts.push(`acima de 2 GB: ${fmt(s.byReason.too_large)}`);
  if (s.byReason.unreadable > 0) parts.push(`ilegíveis: ${fmt(s.byReason.unreadable)}`);
  return `${head}, ${plural(s.total, 'ignorado', 'ignorados')} (${parts.join('; ')})`;
};

// ---------------------------------------------------------------- varredura de pastas

/** Forma estrutural de `FileSystemEntry` (só o que a varredura usa). */
export interface FsEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}

export interface FsFileEntryLike<F> extends FsEntryLike {
  file(success: (file: F) => void, error?: (err: unknown) => void): void;
}

export interface FsDirectoryReaderLike {
  readEntries(success: (entries: FsEntryLike[]) => void, error?: (err: unknown) => void): void;
}

export interface FsDirectoryEntryLike extends FsEntryLike {
  createReader(): FsDirectoryReaderLike;
}

/**
 * Lê TODAS as entradas de uma pasta. `readEntries` devolve no máximo ~100 por chamada e
 * precisa ser chamado de novo até voltar um lote vazio.
 */
export const readAllDirectoryEntries = async (dir: FsDirectoryEntryLike): Promise<FsEntryLike[]> => {
  const reader = dir.createReader();
  const all: FsEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FsEntryLike[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (batch.length === 0) return all;
    all.push(...batch);
  }
};

const readEntryFile = <F>(entry: FsFileEntryLike<F>): Promise<F> =>
  new Promise<F>((resolve, reject) => entry.file(resolve, reject));

export interface CollectResult<F> {
  files: PickedFile<F>[];
  /** Entradas que não puderam ser lidas (caminho), p.ex. caminho longo demais no Windows. */
  unreadable: string[];
}

/**
 * Percorre as entradas soltas (arquivos e pastas misturados), descendo nas subpastas.
 * O caminho relativo vem de `entry.fullPath` sem a "/" inicial — para um arquivo solto é só o
 * nome; dentro de uma pasta, "Pasta/Sub/foto.jpg" (igual a `File.webkitRelativePath`).
 * `onProgress` recebe quantos arquivos já foram encontrados.
 */
export const collectEntries = async <F>(
  roots: FsEntryLike[],
  onProgress?: (found: number) => void,
): Promise<CollectResult<F>> => {
  const files: PickedFile<F>[] = [];
  const unreadable: string[] = [];
  const stack: FsEntryLike[] = [...roots].reverse();
  while (stack.length > 0) {
    const entry = stack.pop() as FsEntryLike;
    const path = cleanRelativePath(entry.fullPath || entry.name);
    if (entry.isDirectory) {
      try {
        const children = await readAllDirectoryEntries(entry as FsDirectoryEntryLike);
        for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
      } catch {
        unreadable.push(path);
      }
      continue;
    }
    if (!entry.isFile) continue;
    try {
      const file = await readEntryFile(entry as FsFileEntryLike<F>);
      files.push({ file, relativePath: path });
      if (onProgress && files.length % 100 === 0) onProgress(files.length);
    } catch {
      unreadable.push(path);
    }
  }
  onProgress?.(files.length);
  return { files, unreadable };
};

/** Entradas ilegíveis viram "ignorados" no resumo. */
export const unreadableAsIgnored = (paths: string[]): IgnoredFile[] =>
  paths.map((path) => {
    const name = path.split('/').pop() || path;
    return { name, path, reason: 'unreadable' as const, ext: fileExtension(name) };
  });
